import { useState, useContext, useEffect } from "react";
import "./styles/profilestyle.css";
import api, { showAlert, AuthContext, API_URL } from "../util/api";
import { useNavigate } from "react-router-dom";

export default function LawyerProfileSetup() {
  const navigate = useNavigate();
  const { User, Doctor, doctorFunction } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [previewImage, setPreviewImage] = useState(null);
  const [idPreviewImage, setIdPreviewImage] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [available, setAvailable] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    gender: "",
    qualification: "",
    lawFirm: "",
    yearsOfExperience: "",
    consultationFee: "",

    // Legal verification
    barNumber: "",
    supremeCourtNumber: "",
    issuingAuthority: "",
    yearOfGraduation: "",
    otherCertifications: "",

    // KYC
    idCardType: "",
    idCardNumber: "",

    // Payment
    bankName: "",
    accountNumber: "",
    accountName: "",
    bl: "",

    // Crypto (optional)
    coinType: "",
    crypto_address: "",

    bio: "",
    profilePicture: null,
    idCardFile: null,
    lawCertificate: null,

    weeklyAvailability: {},
  });

  const daysOfWeek = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
  ];

  /* =============================
     FETCH PRACTICE AREAS
  ============================== */
  const checkPracticeAreas = async () => {
    try {
      const response = await api.get(API_URL + "/specialties");
      setAvailable(response.data.map((s) => s.name));
    } catch (err) {
      console.error("Failed to fetch practice areas", err);
    }
  };

  /* =============================
     HANDLE INPUT
  ============================== */
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        if (name === "profilePicture") setPreviewImage(reader.result);
        if (name === "idCardFile") setIdPreviewImage(reader.result);
        if (name === "lawCertificate") setCertificatePreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  /* =============================
     AVAILABILITY
  ============================== */
  const handleAvailabilityChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      weeklyAvailability: {
        ...prev.weeklyAvailability,
        [day]: {
          ...prev.weeklyAvailability[day],
          [field]: value,
        },
      },
    }));
  };

  /* =============================
     PRACTICE AREAS
  ============================== */
  const addSpeciality = (item) => {
    if (!selected.includes(item)) {
      setSelected([...selected, item]);
      setSearch("");
    }
  };

  const removeSpeciality = (item) => {
    setSelected(selected.filter((s) => s !== item));
  };

  /* =============================
     VALIDATION
  ============================== */
  const validateCurrentStep = () => {
    const container = document.querySelector(`.step-${step}`);
    if (!container) return true;

    const requiredFields = container.querySelectorAll(
      "input[required], select[required], textarea[required]"
    );

    let firstInvalid = null;

    requiredFields.forEach((field) => {
      field.classList.remove("is-invalid");

      const isEmpty =
        field.type === "checkbox"
          ? !field.checked
          : !field.value?.toString().trim();

      if (isEmpty) {
        field.classList.add("is-invalid");
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalid.focus();
      return false;
    }

    return true;
  };

  const nextStep = () => {
    if (validateCurrentStep()) setStep((prev) => prev + 1);
  };

  const prevStep = () => setStep((prev) => prev - 1);

  /* =============================
     SUBMIT
  ============================== */
  const handleSubmit = async (e) => {


    e.preventDefault();
    setSubmitting(true);
    setProgress(10);

    const payload = new FormData();

    for (let key in formData) {
      if (key === "weeklyAvailability") {
        payload.append(
          key,
          JSON.stringify(
            Object.entries(formData.weeklyAvailability).map(([day, v]) => ({
              day,
              available: !!v.available,
              startTime: v.startTime || null,
              endTime: v.endTime || null,
            }))
          )
        );
      } else {
        payload.append(key, formData[key]);
      }
    }

    payload.append("practiceAreas", JSON.stringify(selected));
    payload.append("user_id", User.id);

    try {
      const res = await api.post("/consultant/register", payload, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) =>
          setProgress(Math.round((e.loaded / e.total) * 100)),
      });

      if (res.data.status === "success") {
        doctorFunction(res.data.doctor, res.data.user);
        showAlert("success", "Success", res.data.message);
        navigate("/lawyer-profile/" + res.data.doctor.id);
      } else {
        showAlert("error", "Error", res.data.message);
      }
    } catch (err) {
      showAlert("error", "Error", err.message);
    } finally {
      setSubmitting(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  /* =============================
     INIT
  ============================== */
  useEffect(() => {
    checkPracticeAreas();

    if (!Doctor) return;

    // Weekly availability
    const updatedWeekly = {};


    if (Doctor.weekly_availabilities) {
      Doctor.weekly_availabilities.forEach((item) => {
        updatedWeekly[item.day] = {
          available: item.available,
          startTime: item.start_time || "",
          endTime: item.end_time || "",
        };
      });
    }

    // Merge doctor data into formData
    setFormData((prev) => ({
      ...prev,
      name: Doctor.name || "",
      phone: Doctor.phone || "",
      gender: Doctor.gender || "",
      qualification: Doctor.qualification || "",
      bl: Doctor.bl || "",
      lawFirm: Doctor.lawFirm || "",
      yearsOfExperience: Doctor.yearsOfExperience || "",
      consultationFee: Doctor.consultationFee || "",

      // Legal verification
      barNumber: Doctor.barNumber || "",
      supremeCourtNumber: Doctor.supremeCourtNumber || "",
      issuingAuthority: Doctor.issuingAuthority || "",
      yearOfGraduation: Doctor.yearOfGraduation || "",
      otherCertifications: Doctor.otherCertifications || "",

      // KYC
      idCardType: Doctor.idCardType || "",
      idCardNumber: Doctor.idCardNumber || "",

      // Payment
      bankName: Doctor.bankName || "",
      accountNumber: Doctor.accountNumber || "",
      accountName: Doctor.accountName || "",

      // Crypto (optional)
      coinType: Doctor.coinType || "",
      crypto_address: Doctor.crypto_address || "",

      bio: Doctor.bio || "",
      profilePicture: Doctor.profilePicture || null,
      idCardFile: Doctor.idCardFile || null,
      lawCertificate: Doctor.lawCertificate || null,

      weeklyAvailability: updatedWeekly,
    }));

    setPreviewImage(Doctor.profilePicture || null);
    setIdPreviewImage(Doctor.idCardFile || null);
    setCertificatePreview(Doctor.lawCertificate || null);

    // Specialties
    try {
      setSelected(JSON.parse(Doctor.practiceAreas || "[]"));
    } catch (err) {
      console.error("Failed to parse specialties", err);
    }


  }, [Doctor]);




  useEffect(() => {
    setFiltered(
      available.filter(
        (s) => !selected.includes(s) && s.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, selected, available]);


  return (
    <div className="container-fluid form-container">
      <div className="form-card-modern">
        <h2 className="form-title">Lawyer Profile Setup</h2>




        {/* Progress */}
        <div className="progressbar">
          <div onClick={() => setStep(1)}>
            <span className={`step ${step >= 1 ? "active" : ""}`}>1</span>
            <span>Personal</span>
          </div>
          <div onClick={() => setStep(2)}>
            <span className={`step ${step >= 2 ? "active" : ""}`}>2</span>
            <span>Verification</span>
          </div>
          <div onClick={() => setStep(3)}>
            <span className={`step ${step >= 3 ? "active" : ""}`}>3</span>
            <span>Fees</span>
          </div>
          <div onClick={() => setStep(4)}>
            <span className={`step ${step >= 4 ? "active" : ""}`}>4</span>
            <span>Availability</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* STEP 1 */}
          {step === 1 && (
            <div className="step-1 step-section">
              <h4>Personal Info</h4>

              <div className="profile-upload-modern">
                <img
                  src={previewImage || "/logo_back_remove.png"}
                  alt="profile"
                />
                <label htmlFor="profilePicture" className="upload-label">
                  📷 Upload
                </label>
                <input
                  type="file"
                  id="profilePicture"
                  name="profilePicture"
                  hidden
                  onChange={handleChange}
                />
              </div>

              <label>Email</label>

              <input readOnly value={User.email} />

              <label>Full Name</label>



              <input name="name" required
                placeholder="Full Name"
                value={formData.name} onChange={handleChange} />

              <label>Phone</label>
              <input name="phone" required placeholder="Phone"
                value={formData.phone} onChange={handleChange} />

              <label>Gender</label>

              <select name="gender" value={formData.gender}
                required onChange={handleChange}>
                <option value="">Gender</option>
                <option>Male</option>
                <option>Female</option>
              </select>

              <label>University Attended </label>

              <input name="qualification" required placeholder="University Attended" value={formData.qualification} onChange={handleChange} />



              <label>Year of Graduation</label>

              <input name="yearOfGraduation" required placeholder="Year of Graduation" value={formData.yearOfGraduation} onChange={handleChange} />



              <label> Year of Call to the Bar </label>

              <input name="bl" required placeholder="Year of Call" value={formData.bl} onChange={handleChange} />



              <label> Other Professional Certifications </label>

              <input name="otherCertifications" required placeholder="Other Professional Certifications" value={formData.otherCertifications} onChange={handleChange} />







              <label>Law Firm</label>
              <input name="lawFirm" placeholder="Law Firm" value={formData.lawFirm} onChange={handleChange} />
              <label>Years of Experience</label>
              <input name="yearsOfExperience" required placeholder="Years of Experience" value={formData.yearsOfExperience} onChange={handleChange} />

              <label>Area of Practice</label>

              {/* Areas of Practice */}
              <div className="speciality-section">
                <input
                  type="text"
                  placeholder="Search areas of practice..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="speciality-dropdown">
                  {filtered.length === 0 && <p>No match found</p>}
                  {filtered.map((s) => (
                    <div
                      key={s}
                      onClick={() => addSpeciality(s)}
                      className="speciality-item"
                    >
                      {s}
                    </div>
                  ))}
                </div>
                <div className="selected-specialities">
                  {selected.map((s) => (
                    <span key={s} className="badge">
                      {s}{" "}
                      <button onClick={() => removeSpeciality(s)}>&times;</button>
                    </span>
                  ))}
                </div>
              </div>

              <label>About You</label>

              <textarea name="bio" required placeholder="About you" value={formData.bio} onChange={handleChange} />

              <div className="step-nav">
                <button type="button" onClick={nextStep} className="action-button">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="step-2 step-section">
              <h4>Verification</h4>

              <label>Bar Number</label>

              <input name="barNumber" required placeholder="NBA Bar Number" value={formData.barNumber} onChange={handleChange} />

              <label>Supreme Court Number</label>
              <input name="supremeCourtNumber" placeholder="Supreme Court Number" value={formData.supremeCourtNumber} onChange={handleChange} />

              <label>Issuing Authority</label>
              <input name="issuingAuthority" required placeholder="Issuing Authority" value={formData.issuingAuthority} onChange={handleChange} />

              <label>ID Card Type</label>

              <select name="idCardType" value={formData.idCardType} onChange={handleChange}>
                <option>ID Type</option>
                <option>National ID</option>
                <option>Passport</option>
                <option>Driver's License</option>
              </select>
              <label>ID Card Number</label>

              <input name="idCardNumber" placeholder="ID Number" value={formData.idCardNumber} onChange={handleChange} />

              <label>Upload ID Card</label>
              <input type="file" name="idCardFile" onChange={handleChange} />
              {idPreviewImage && <img src={idPreviewImage} width="150" />}

              <label>Upload Call to Bar Certificate</label>
              <input type="file" name="lawCertificate" onChange={handleChange} />
              {certificatePreview && <img src={certificatePreview} width="150" />}

              <div className="step-nav">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                &nbsp;
                <button type="button" onClick={nextStep} className="action-button">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="step-3 step-section">
              <h4>Fees</h4>
              <input name="consultationFee" required  type="number"
               placeholder="Consultation Fee" value={formData.consultationFee} onChange={handleChange} />



              <div className="step-nav">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                &nbsp;
                <button type="button" onClick={nextStep} className="action-button">
                  Continue
                </button>
              </div>


            </div>
          )}

          {/* STEP 4 */}
          {/* ------------------ STEP 4 ------------------ */}
          {step === 4 && (
            <div className="step-4 step-section">
              <h4>Weekly Availability</h4>
              <div className="availability-grid">
                {daysOfWeek.map((day) => (
                  <div key={day} className="availability-card-modern">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={formData.weeklyAvailability[day]?.available || false}
                        onChange={(e) =>
                          handleAvailabilityChange(day, "available", e.target.checked)
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>{day}</span>
                    {formData.weeklyAvailability[day]?.available && (
                      <div className="time-inputs">
                        <input
                          type="time"
                          value={formData.weeklyAvailability[day]?.startTime || ""}
                          onChange={(e) =>
                            handleAvailabilityChange(day, "startTime", e.target.value)
                          }
                        />
                        <input
                          type="time"
                          value={formData.weeklyAvailability[day]?.endTime || ""}
                          onChange={(e) =>
                            handleAvailabilityChange(day, "endTime", e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {submitting && (
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                  <p>{progress}%</p>
                </div>
              )}

              <div className="step-nav">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                &nbsp;
                <button type="submit" disabled={submitting} className="action-button">
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
}